import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BadgeCheck, ShieldCheck, Upload, Clock, XCircle, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Verify() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/verification/status"); setStatus(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/verification/aadhaar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.is_verified) toast.success("You're verified! ✓");
      else toast.info("Submitted for review");
      await refresh();
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const verified = user?.is_verified || status?.is_verified;
  const latest = status?.latest;

  return (
    <div className="container-px py-8 max-w-2xl">
      <Link to="/app/profile" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4" data-testid="verify-back-link"><ArrowLeft className="w-4 h-4 mr-1"/>Back to profile</Link>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <span className="label-cap">Trust &amp; Safety</span>
      </div>
      <h1 className="font-heading text-4xl text-primary">ID Verification</h1>
      <p className="text-muted-foreground mt-2">Get a verified badge by uploading a government photo ID (Aadhaar, PAN, Voter ID, Driving Licence or Passport). It's checked instantly and securely.</p>

      {verified ? (
        <div className="card-soft p-8 mt-8 text-center" data-testid="verify-verified-state">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <BadgeCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl text-primary">You're verified</h2>
          <p className="text-muted-foreground mt-2">Your profile now displays a verified badge, boosting trust with matches.</p>
        </div>
      ) : (
        <div className="card-soft p-6 mt-8 space-y-5" data-testid="verify-upload-state">
          {latest?.status === "pending" && (
            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-xl p-3 text-sm" data-testid="verify-pending-banner">
              <Clock className="w-4 h-4" /> Your last submission is under manual review. You can re-upload a clearer ID below.
            </div>
          )}
          {latest?.status === "rejected" && (
            <div className="flex items-center gap-2 text-destructive bg-destructive/10 rounded-xl p-3 text-sm" data-testid="verify-rejected-banner">
              <XCircle className="w-4 h-4" /> Your previous submission was not approved. Please upload a valid, clear ID.
            </div>
          )}

          <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-2xl p-10 cursor-pointer hover:bg-muted/40 transition-colors" data-testid="verify-dropzone">
            <Upload className="w-8 h-8 text-primary mb-3" strokeWidth={1.5} />
            <span className="font-medium">{uploading ? "Verifying…" : "Upload your ID photo"}</span>
            <span className="text-xs text-muted-foreground mt-1">JPG, PNG or WEBP · max 8 MB</span>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} disabled={uploading} data-testid="verify-file-input" />
          </label>

          <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Your ID is stored privately and used only for verification. It is never shown on your public profile.
          </p>

          {result && (
            <div className={`rounded-xl p-4 text-sm ${result.is_verified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="verify-result">
              <div className="font-medium">{result.is_verified ? "Verified ✓" : "Pending review"}</div>
              <div className="mt-1 opacity-90">{result.reason}{result.doc_type ? ` · Detected: ${result.doc_type}` : ""}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
